import { defineNuxtPlugin } from 'nuxt/app'

import '../../public/_static-files/20388ba0-3f7b-11f1-9cfc-33ad2e0000001.js'

/**
 * Простая стратегия разрешения промиса
 * Используется для базового разрешения промиса без дополнительной логики
 */
export class ResolveStrategySync {
  constructor(_, originalResolve) {
    this.originalResolve = originalResolve
  }

  /**
   * Основной метод, который резолвит промис.
   * В данной стратегии он просто вызывает оригинальный resolve.
   */
  resolve() {
    return this.originalResolve()
  }

  /**
   * Метод вызывается вконце синхронной части инициализации GIB.
   * В данной стратегии промис резолвится синхронно, поэтому
   * вконце синхронной части инита розолвим промис.
   */
  afterSyncInit() {
    return this.resolve()
  }

  /**
   * Метод вызывается в случае ошибки инициализации GIB.
   * Вызывает resolve для завершения процесса.
   */
  onSyncInitError() {
    return this.resolve()
  }
}

/**
 * Стратегия разрешения промиса на основе таймера
 * Используется для отслеживания ошибки инициализации GUN
 * Если за 15 секунд не инициализируется GUN, то считаем, что GUN не доступен
 */
export class ResolveStrategyAsync {
  constructor(context, originalResolve, options) {
    this.context = context
    this.options = options
    this.originalResolve = originalResolve

    this.timer = this.createTimer()
    this.customXMLHttpRequest = this.createXMLHttpRequest()
    this.customGIBOnFlowResponse = this.createGIBOnFlowResponse()

    this.initialize()
  }

  /**
   * Основной метод, который резолвит промис. Перед резолвом
   * выполняет очистку: сбрасывает таймер и восстанавливает
   * оригинальный метод XMLHttpRequest.
   */
  resolve() {
    this.timer.reset()
    this.customXMLHttpRequest.unpatch()

    this.originalResolve()
  }

  /**
   * Метод вызывается вконце синхронной части инициализации GIB.
   * В данной стратегии промис резолвится асинхронно, поэтому
   * вконце синхронной части инита ничего не розолвим.
   */
  afterSyncInit() {
    return false
  }

  /**
   * Метод вызывается в случае ошибки инициализации GIB.
   * Вызывает resolve для завершения процесса.
   */
  onSyncInitError() {
    return this.resolve()
  }

  /**
   * Запускает все необходимые механизмы: устанавливает таймер,
   * патчит XMLHttpRequest и gibOnFlowResponse
   */
  initialize() {
    this.timer.set()
    this.customXMLHttpRequest.patch()
    this.customGIBOnFlowResponse.patch()
  }

  /**
   * Создает и возвращает объект таймера. Таймер отслеживает время
   * инициализации GUN и вызывает resolve с ошибкой, если
   * инициализация занимает больше 15 секунд.
   */
  createTimer() {
    let timerId = null

    /**
     * Устанавливает таймер на 15 секунд. Если за это время
     * не будет вызван reset, таймер сработает, залогирует
     * ошибку и вызовет resolve.
     */
    const set = () => {
      timerId = setTimeout(() => {
        const errorApm = new Error(`Ошибка инициализации GUN за 15 секунды`)
        errorApm.name = 'app:init(gun)'

        this.context.$apm.captureError(errorApm)

        this.resolve()
      }, 15000)
    }

    /**
     * Сбрасывает таймер, предотвращая его срабатывание.
     */
    const reset = () => {
      clearTimeout(timerId)
      timerId = null
    }

    return {
      set,
      reset,
    }
  }

  /**
   * Патчит глобальный XMLHttpRequest для перехвата запросов к GUN API.
   * Если запрос завершается с ошибкой (статус >= 400),
   * логирует ошибку и вызывает resolve.
   */
  createXMLHttpRequest() {
    const self = this
    const originalOpen = XMLHttpRequest.prototype.open

    /**
     * Подменяет оригинальный метод XMLHttpRequest.prototype.open.
     * Новая реализация добавляет обработчик 'load' для отслеживания
     * статуса ответа от GUN API.
     */
    const patch = () => {
      XMLHttpRequest.prototype.open = function (method, url, ...args) {
        if (typeof url === 'string' && url.includes(self.options.gunApiPath)) {
          this.addEventListener('load', function () {
            if (this.status >= 400) {
              const error = {
                status: this.status,
                responseText: this.responseText,
              }

              const errorApm = new Error(`Ошибка инициализации GUN api`)
              errorApm.name = 'app:init(gun)'
              errorApm.errorData = JSON.stringify(error)
              self.context.$apm.captureError(errorApm)

              self.resolve()
            }
          })
        }

        return originalOpen.apply(this, [method, url, ...args])
      }
    }

    /**
     * Восстанавливает оригинальный метод XMLHttpRequest.prototype.open,
     * убирая кастомный обработчик.
     */
    const unpatch = () => {
      if (XMLHttpRequest.prototype.open !== originalOpen) {
        XMLHttpRequest.prototype.open = originalOpen
      }
    }

    return {
      patch,
      unpatch,
    }
  }

  /**
   * Патчит глобальный метод window.gibOnFlowResponse. Этот метод
   * вызывается при успешном ответе от GUN API, после чего
   * он вызывает resolve для завершения процесса.
   */
  createGIBOnFlowResponse() {
    const patch = () => {
      window.gibOnFlowResponse = () => {
        window.gibOnFlowResponse = () => {}

        this.resolve()
      }
    }

    return {
      patch,
    }
  }
}

export default defineNuxtPlugin((context) => {
  // Блокируем дальнейшую загрузку пока не инициализируем GUN
  return new Promise((resolve) => {
    // проверяем домены
    const isAllowedDomain = window.location.host.split('.')[0] === 'prize'

    if (context.$gaApp.config.get('dev') || !isAllowedDomain) {
      return resolve()
    }

    const gunApiPath = '/front/api/event'
    const resolveStrategy = 'sync'

    // Создаем класс резолва на основе конфига
    const ResolverClass = {
      sync: ResolveStrategySync,
      async: ResolveStrategyAsync,
    }[resolveStrategy]

    const resolver = new ResolverClass(context, resolve, { gunApiPath })

    try {
      // В зависимости от домена, строим строку cid
      const cid = (() => {
        /**
         * PROD
         * w-goldapple
         */
        return 'w-goldapple'
      })()

      // Инит скрипта gun
      window.gib.init({
        cid,
        backUrl: gunApiPath,
        gafUrl: '//ru.id.facct.ru/id.html',
      })

      // Устанавливаем данные пользователя в GUN
      const setUserIdentity = () => {
        if (!window.gib) return

        const { externalData, isAuthorized } = context.$gaApp.stores.main.user

        const sessionID = externalData?.sessionID ?? null
        const userAuthStatus = isAuthorized
          ? window.gib.IS_AUTHORIZED
          : window.gib.IS_GUEST

        window.gib.setIdentity(sessionID)
        window.gib.setSessionID(sessionID)
        window.gib.setAuthStatus(userAuthStatus)
      }

      // Сбрасываем данные пользователя в GUN
      const resetUserIdentity = () => {
        if (!window.gib) return

        // TODO: дефолтное значение нужно брать также из шопа
        // возможна проблема, когда в игре разлогинен, а в шопе нет
        // и тогда в шопе не сбросится значение
        const sessionId = null
        const userAuthStatus = window.gib.IS_GUEST

        window.gib.setIdentity(sessionId)
        window.gib.setSessionID(sessionId)
        window.gib.setAuthStatus(userAuthStatus)
      }

      // Устанавливаем данные пользователя на этапе инита
      const unsubscribeInitial = context.$gaApp.eventBus.subscribe(
        'module/app/router/page-created',
        () => {
          setUserIdentity()
          unsubscribeInitial()
        },
      )

      // Подписываемся на события login/logout
      context.$gaApp.eventBus.subscribe('user/auth/login', setUserIdentity)
      context.$gaApp.eventBus.subscribe('user/auth/logout', resetUserIdentity)

      resolver.afterSyncInit()
    } catch (error) {
      console.log('Ошибка инициализации GUN', error)

      const errorApm = new Error(`Ошибка инициализации GUN`)
      errorApm.name = 'app:init(gun)'
      errorApm.errorData = JSON.stringify(error)

      context.$apm.captureError(errorApm)

      resolver.onSyncInitError()
    }
  })
})
